import React, { useContext, useMemo } from 'react';

import cloneDeep from 'lodash/cloneDeep';
import PropTypes from 'prop-types';

import BuilderCollectionContext from '~/builder/components/BuilderCollectionContext';
import getBuilderStateContentVariantData from '~/builder/utils/getBuilderStateContentVariantData';
import { useBuilderCollection } from '~/builder/utils/useBuilderCollection';
import GridNode from '~/grid/components/GridNode';
import useGender from '~/shared/utils/useGender';

import BuilderDisabledCollection from '../BuilderDisabledCollection';
import { Context as BuilderTabsContext } from '../BuilderTabs/BuilderTabs';

const BuilderCollection = ({
  builderBlock,
  builderState: { state: builderStateData, content: builderStateContent },
}) => {
  const { gender } = useGender();
  const builderTabContext = useContext(BuilderTabsContext);
  const isTabGrid = builderTabContext?.isBuilderTabs;
  const builderStateContentVariantData = getBuilderStateContentVariantData({
    builderStateContent,
  });
  const automaticSizeFiltering =
    builderStateContentVariantData?.automaticSizeFiltering;
  const automaticGenderFiltering =
    builderStateContentVariantData?.enableBuilderGenderAutofilter;

  // this is a proxy which is the reason for the cloneDeep
  const ssrCollectionContent = useMemo(() => {
    const collection = builderStateData?.collections?.find((c) => {
      return builderBlock?.id === c.id;
    });
    return cloneDeep(collection?.content);
  }, [builderBlock.id, builderStateData.collections]);

  const clientCollectionContent = useBuilderCollection({
    options: builderBlock?.component?.options,
    serverSideData: ssrCollectionContent,
    isTabGrid,
    automaticSizeFiltering,
    automaticGenderFiltering,
  });

  const collectionContent = ssrCollectionContent || clientCollectionContent;

  if (collectionContent?.isCollectionDisabled) {
    return (
      <BuilderDisabledCollection
        reason={collectionContent?.collectionDisabledReason}
      />
    );
  } else if (
    collectionContent?.initialProducts?.debugData?.algorithmTrace?.action ===
    'halt:thresholdNotMet'
  ) {
    return <BuilderDisabledCollection reason="Algorithm threshold not met." />;
  }

  return (
    <BuilderCollectionContext.Provider value={collectionContent}>
      <GridNode
        gender={gender}
        isCarouselGrid={collectionContent?.isCarousel}
        isTabGrid={isTabGrid}
        {...collectionContent}
        data-builder-component-type="collection"
      />
    </BuilderCollectionContext.Provider>
  );
};

BuilderCollection.propTypes = {
  builderBlock: PropTypes.object,
  builderState: PropTypes.object,
};

export default BuilderCollection;
